<template>
	<div
		class="relative flex aspect-square items-center justify-center overflow-hidden"
	>
		<Teleport to="body" :disabled="rendered">
			<DotLottieVue
				v-if="data"
				:data="data"
				:autoplay="autoplay"
				:loop="loop"
				class="h-full w-full transition-opacity"
				:class="{
					'opacity-0': !loaded,
					'fixed top-0 left-0 opacity-0': !rendered
				}"
				:style="{
					width: !rendered ? `${forceSize}px` : undefined,
					height: !rendered ? `${forceSize}px` : undefined
				}"
				ref="lottieRef"
				:renderConfig="{ autoResize: false, freezeOnOffscreen: true }"
			/>
		</Teleport>
		<div
			class="absolute inset-0 flex items-center justify-center"
			v-if="!loaded"
		>
			<slot>
				<StickerPreloader v-if="preloader" :preloader="preloader" />
				<div
					v-else
					class="bg-fill-secondary h-8 w-8 animate-pulse rounded-full"
				/>
			</slot>
		</div>
	</div>
</template>
<script setup lang="ts">
import { DotLottieVue, setWasmUrl } from '@lottiefiles/dotlottie-vue'
import wasmUrl from '@lottiefiles/dotlottie-web/dist/dotlottie-player.wasm?url'
import { ref, watch, nextTick } from 'vue'
import { gunzip } from 'fflate'
import { sleep } from '@/utils/sleep'
import StickerPreloader from './StickerPreloader.vue'

setWasmUrl(wasmUrl)

const data = ref()
const loaded = ref(false)

const props = defineProps<{
	src: string
	loop?: boolean
	autoplay?: boolean
	forceSize?: number
	preloader?: string
}>()

const emit = defineEmits<{
	(e: 'loaded'): void
	(e: 'error'): void
}>()

const rendered = ref(true)

const onLoad = () => {
	loaded.value = true
	emit('loaded')

	if (!lottieRef.value) {
		return
	}

	const instance = lottieRef.value.getDotLottieInstance()

	if (!instance) {
		return
	}

	instance.removeEventListener('loadError', onError)
}

const onError = () => {
	loaded.value = false
	emit('error')

	if (!lottieRef.value) {
		return
	}

	const instance = lottieRef.value.getDotLottieInstance()

	if (!instance) {
		return
	}

	instance.removeEventListener('load', onLoad)
}

const fetchAndParseTgs = async (url: string) => {
	try {
		// Fetch file from the URL
		const response = await fetch(url)

		if (!response.ok) {
			throw new Error('Failed to fetch')
		}

		// Convert to ArrayBuffer
		const arrayBuffer = await response.arrayBuffer()

		// Decompress (gunzip)
		const decompressed = await new Promise<AllowSharedBufferSource>(
			(res, rej) =>
				gunzip(new Uint8Array(arrayBuffer), (error, data) => {
					if (error) {
						return rej(error)
					}

					res(data)
				})
		)

		// Parse JSON
		const json = new TextDecoder().decode(decompressed)

		data.value = json

		await nextTick()

		if (!lottieRef.value) {
			return
		}

		const instance = lottieRef.value.getDotLottieInstance()

		if (!instance) {
			return
		}

		instance.addEventListener('loadError', onError, { once: true })
		instance.addEventListener('load', onLoad, { once: true })

		if (props.forceSize) {
			rendered.value = false

			await nextTick()
			await sleep(100)

			resize()

			await nextTick()
			await sleep(100)

			rendered.value = true
		}
	} catch {
		emit('error')
	}
}

watch(
	() => props.src,
	() => {
		if (!props.src) {
			return
		}

		fetchAndParseTgs(props.src)
	},
	{ immediate: true }
)

const lottieRef = ref()

const resize = () => {
	if (!lottieRef.value) {
		return
	}

	const instance = lottieRef.value.getDotLottieInstance()

	if (!instance) {
		return
	}

	instance.resize()
}

const play = () => {
	if (!lottieRef.value) {
		return
	}

	const instance = lottieRef.value.getDotLottieInstance()

	if (!instance) {
		return
	}

	instance.play()
}

defineExpose({
	resize,
	play
})
</script>
